import type { PermitTypeContext } from "@offstreetparking/domain/rules";
import { DateTime } from "luxon";
import { AccessibleContent } from "../../accessibility";
import { formatDate, formatTimeOfDay } from "../../formatters";
import { ArrowRightIcon } from "../../icons/ArrowRightIcon";
import { DateRangeIcon } from "../../icons/DateRangeIcon";
import { TimeIcon } from "../../icons/TimeIcon";
import { useDesignSystemI18n } from "../../providers/DesignSystemI18nContext";
import { useLocale } from "../../providers/DesignSystemLocaleContext";
import { Text } from "../../typography";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

type ParkingOptionProps = {
	timezone: string;
	isInstant: boolean;
	context?: PermitTypeContext | null;
};

type DayOfWeekField = NonNullable<NonNullable<PermitTypeContext["start"]>["dayOfWeek"]>;

const toDayArray = (value: DayOfWeekField | undefined): string[] =>
	Array.isArray(value) ? value : value ? [value] : [];

const parseDateInTimezone = ({ date, timezone }: { date: string; timezone: string }) =>
	DateTime.fromISO(date, { zone: timezone }).toJSDate();

const splitDurationFixed = (durationInMinutes: number | undefined) => {
	const total = durationInMinutes ?? 0;
	return {
		weeks: Math.floor(total / (24 * 60 * 7)),
		days: Math.floor((total % (24 * 60 * 7)) / (24 * 60)),
		hours: Math.floor((total % (24 * 60)) / 60),
		minutes: total % 60,
	};
};

const DayOfWeekBadges = ({ days, labels }: { days: string[]; labels: Record<(typeof DAY_KEYS)[number], string> }) => {
	const lowerDays = days.map((d) => d.toLowerCase());
	return (
		<div className="flex flex-wrap gap-1">
			{DAY_KEYS.filter((key) => lowerDays.includes(key)).map((key) => (
				<div key={key} className="flex items-center justify-center rounded-md bg-info-faded px-2 py-0.5">
					<Text size="xs" weight="medium" className="text-info">
						{labels[key]}
					</Text>
				</div>
			))}
		</div>
	);
};

export const RulesPreview = ({ timezone, isInstant, context }: ParkingOptionProps) => {
	const { daysOfWeekShort, parkingOption } = useDesignSystemI18n();
	const { locale } = useLocale();
	const start = context?.start;
	const expiry = context?.expiry;
	const startDayOfWeek = toDayArray(start?.dayOfWeek);
	const expiryDayOfWeek = toDayArray(expiry?.dayOfWeek);
	const duration = splitDurationFixed(context?.duration?.fixed);
	const hasDuration = duration.minutes || duration.hours || duration.days || duration.weeks;
	const isFullyInstant = (!!start?.isDateNow && !!start?.isTimeNow) || isInstant;

	const getStartDateContent = () => {
		if (start?.date) {
			return formatDate({
				date: parseDateInTimezone({ date: start.date, timezone }),
				formatType: "Abbreviated_Date",
				timezone,
			});
		}

		if (startDayOfWeek.length) {
			return parkingOption.specificDays;
		}

		if (start?.isDateNow || isFullyInstant) {
			return parkingOption.whenRegistered;
		}

		return parkingOption.userSelects;
	};

	const getStartTimeContent = () => {
		if (start?.time) {
			return formatTimeOfDay({ time: start.time, locale });
		}

		if (start?.isTimeNow || isFullyInstant) {
			return parkingOption.whenRegistered;
		}

		return parkingOption.userSelects;
	};

	const getExpiryDateContent = () => {
		if (expiryDayOfWeek.length) {
			return parkingOption.specificDays;
		}
		if (expiry?.date) {
			return formatDate({
				date: parseDateInTimezone({ date: expiry.date, timezone }),
				formatType: "Abbreviated_Date",
				timezone,
			});
		}

		if (hasDuration) {
			return parkingOption.afterDuration;
		}

		if (expiry?.sameDay) {
			return parkingOption.sameDay;
		}

		if (expiry?.inDays) {
			return parkingOption.afterDays(expiry.inDays);
		}

		return parkingOption.userSelects;
	};

	const getExpiryTimeContent = () => {
		if (expiry?.time) {
			return formatTimeOfDay({ time: expiry.time, locale });
		}

		if (hasDuration) {
			return parkingOption.duration({
				minutes: duration.minutes,
				hours: duration.hours,
				days: duration.days,
				weeks: duration.weeks,
			});
		}

		return parkingOption.userSelects;
	};

	const startLabel = isFullyInstant ? getStartDateContent() : `${getStartDateContent()} ${getStartTimeContent()}`;

	return (
		<AccessibleContent label={`${startLabel} – ${getExpiryDateContent()} ${getExpiryTimeContent()}`}>
			<div className="space-y-2">
				<div className="flex items-start gap-2">
					<DateRangeIcon className="mt-[1px] size-4" />
					<div className="flex flex-col items-start gap-1">
						<div className="flex items-center gap-2">
							<Text size="sm" weight="bold">
								{getStartDateContent()}
							</Text>
						</div>
						{!!startDayOfWeek.length && <DayOfWeekBadges days={startDayOfWeek} labels={daysOfWeekShort} />}
						{!isFullyInstant && (
							<div className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1">
								<TimeIcon className="size-3 min-h-3 min-w-3" />
								<Text size="xs">{getStartTimeContent()}</Text>
							</div>
						)}
					</div>

					<ArrowRightIcon className="size-4 self-center" />

					<div className="flex flex-col items-start gap-1">
						<div className="flex items-center gap-2">
							<Text size="sm" weight="bold">
								{getExpiryDateContent()}
							</Text>
						</div>
						{!!expiryDayOfWeek.length && (
							<DayOfWeekBadges days={expiryDayOfWeek} labels={daysOfWeekShort} />
						)}
						<div className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1">
							<TimeIcon className="size-3 min-h-3 min-w-3" />
							<Text size="xs">{getExpiryTimeContent()}</Text>
						</div>
					</div>
				</div>
			</div>
		</AccessibleContent>
	);
};
